import { z } from 'zod';
/**
 * Lo que manda el telefono del ingeniero: la bitacora de eventos y la hoja de
 * termino con su acuse.
 *
 * Las dos formas salen de las migraciones 0019 y 0020 y se escriben UNA vez
 * aqui, porque la app movil, la consola y la API tienen que coincidir en los
 * limites. Cuando los limites se escriben dos veces, el telefono acepta una
 * nota de 4000 caracteres que el servidor rechaza, y el ingeniero pierde la
 * captura en el unico momento en que no la puede repetir: cuando ya se fue
 * del sitio.
 */
/* ------------------------------- Eventos --------------------------------- */
/**
 * Los trece tipos del enum `evento_tipo` (0019), en el orden en que ocurren
 * en campo. No son los estados del ticket: el estado es uno a la vez y lo
 * mueve la operacion, esto es un diario que se acumula y admite repeticiones.
 */
export const TIPOS_EVENTO = [
    'EVM_ACEPTA',
    'EVM_RUTA',
    'EVM_ARRIBO',
    'EVM_INICIO',
    'EVM_FOTO',
    'EVM_PAUSA',
    'EVM_FIRMA',
    'EVM_CIERRE',
    'EVM_SALIDA',
    'EVM_INCIDENCIA',
    'EVM_COMENTARIO',
    'EVM_INVENTARIO',
    'EVM_REPROGRAMA',
];
export const ORIGENES_EVENTO = ['web', 'movil'];
/** Cuantos eventos entran en un solo lote. Un dia de trabajo sin cobertura
 *  cabe de sobra en 200, y el tope acota el cuerpo de la peticion. */
export const MAX_EVENTOS_POR_LOTE = 200;
/**
 * La clave de idempotencia la genera el DISPOSITIVO, una por evento encolado,
 * y la repite tal cual en cada reintento. Es la pieza que hace seguro
 * reenviar la cola completa.
 *
 * El alfabeto es el del CHECK de la 0019; el largo se acota a 64 y no a los
 * 128 que admite la columna, porque 64 ya sobran para un ULID o un UUID con
 * prefijo y un limite mas corto es un cuerpo mas chico en una red de sitio.
 */
export const esquemaIdempotencia = z
    .string()
    .trim()
    .regex(/^[A-Za-z0-9._:-]{8,64}$/);
/**
 * Un evento de la cola del telefono.
 *
 * `origen` NO esta aqui a proposito: lo fija el servidor. Si lo mandara el
 * cliente, una captura hecha a mano desde la consola podria presentarse como
 * marcada por el ingeniero en el sitio, y la columna existe justamente para
 * poder separar las dos cosas en una auditoria.
 *
 * `recibido_en` tampoco: es la hora del servidor y la pone la base.
 */
export const eventoMovil = z
    .object({
    tipo: z.enum(TIPOS_EVENTO),
    /* La hora del DISPOSITIVO. No se compara contra now(): el reloj de un
       telefono se desfasa, y rechazar el evento por venir adelantado tira el
       dato en vez de dejarlo revisable. */
    ocurrido_en: z.string().datetime({ offset: true }),
    lat: z.number().min(-90).max(90).optional(),
    lon: z.number().min(-180).max(180).optional(),
    exactitud_m: z.number().int().min(0).max(100_000).optional(),
    nota: z.string().trim().max(2000).optional(),
    idempotencia: esquemaIdempotencia,
})
    /* Las dos restricciones de la 0019, de este lado: una coordenada sola no
       ubica nada y una exactitud sin coordenada no describe nada. Se validan
       aqui para que el telefono reciba el motivo y no un error de la base. */
    .refine((e) => (e.lat === undefined) === (e.lon === undefined), {
    message: 'la latitud y la longitud viajan juntas',
    path: ['lat'],
})
    .refine((e) => e.exactitud_m === undefined || e.lat !== undefined, {
    message: 'la exactitud exige una coordenada',
    path: ['exactitud_m'],
});
/**
 * El lote que sube la cola.
 *
 * `idc_id` es del lote y no de cada evento: una cola pertenece al ingeniero
 * que la grabo. El servidor lo usa para comprobar que el ticket si es suyo,
 * que es lo que impide que un telefono siembre eventos en servicios ajenos.
 */
export const loteEventos = z.object({
    idc_id: z.string().uuid(),
    eventos: z.array(eventoMovil).min(1).max(MAX_EVENTOS_POR_LOTE),
});
/* ---------------------------- Hoja de termino ---------------------------- */
/** El minimo de la 0020: veinte caracteres descartan "ok" y "listo". */
export const MIN_RESUMEN_HOJA = 20;
/** El acuse admite hasta 20 MB, como el CHECK de `acuse_bytes`. */
export const MAX_BYTES_ACUSE = 20 * 1024 * 1024;
/** Mismo patron que el CHECK de `hojas_servicio.correo`. */
const PATRON_CORREO = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
/**
 * La hoja completa, para cuando ya se sabe que no falta nada.
 *
 * La validacion de "que falta" NO se hace con este esquema: ver
 * `faltantesDeHoja`.
 */
export const hojaDeTermino = z.object({
    resumen: z.string().trim().min(MIN_RESUMEN_HOJA).max(8000),
    observaciones: z.string().trim().max(4000).optional(),
    responsable: z.string().trim().min(3).max(120),
    puesto: z.string().trim().min(2).max(80).optional(),
    correo: z.string().trim().toLowerCase().max(254).regex(PATRON_CORREO).optional(),
    /* La firma como ruta vectorial, no como imagen: pesa cientos de bytes,
       viaja por una red de sitio y se reimprime nitida en la hoja impresa. */
    firma_svg: z.string().min(40).max(262_144),
    horas: z.number().positive().max(999.99).optional(),
});
/** Forma laxa: todo opcional, para poder revisar campo por campo en vez de
 *  rebotar la captura completa con un unico "formato invalido". */
const hojaLaxa = z
    .object({
    resumen: z.unknown(),
    responsable: z.unknown(),
    correo: z.unknown(),
    firma_svg: z.unknown(),
})
    .partial();
const texto = (valor) => (typeof valor === 'string' ? valor.trim() : '');
/**
 * TODO lo que falta, no el primero.
 *
 * Es la misma decision que la tabla de transiciones de `tickets.ts`: quien
 * captura una hoja esta en un sitio, muchas veces sin red, y un ida y vuelta
 * por campo es un ida y vuelta por campo con riesgo de perder la captura. Se
 * revisa todo junto y se devuelve la lista entera.
 */
export function faltantesDeHoja(cuerpo) {
    const analisis = hojaLaxa.safeParse(cuerpo ?? {});
    const d = analisis.success ? analisis.data : {};
    const faltan = [];
    const resumen = texto(d.resumen);
    if (resumen.length < MIN_RESUMEN_HOJA) {
        faltan.push({
            campo: 'resumen',
            codigo: 'ETK-HS-010',
            motivo: `Describe lo que se ejecuto con al menos ${MIN_RESUMEN_HOJA} caracteres: la hoja sostiene la factura`,
        });
    }
    if (texto(d.responsable).length < 3) {
        faltan.push({
            campo: 'responsable',
            codigo: 'ETK-HS-011',
            motivo: 'Asienta el nombre completo de quien recibe el servicio',
        });
    }
    /* El correo es opcional; lo que no se acepta es uno mal escrito. Guardarlo
       invalido es peor que no tenerlo: el acuse se manda a una direccion que no
       existe y nadie se entera. */
    const correo = texto(d.correo);
    if (correo.length > 0 && !PATRON_CORREO.test(correo)) {
        faltan.push({
            campo: 'correo',
            codigo: 'ETK-HS-012',
            motivo: 'El correo de quien recibe no tiene forma de correo',
        });
    }
    if (texto(d.firma_svg).length < 40) {
        faltan.push({
            campo: 'firma',
            codigo: 'ETK-HS-013',
            motivo: 'Recaba la firma de quien recibe en la pantalla del telefono',
        });
    }
    return faltan;
}
/* -------------------------------- Acuse ---------------------------------- */
/**
 * El acuse fisico: la foto o el PDF de la hoja impresa y firmada de puno y
 * letra que el cliente devuelve.
 *
 * Solo se DECLARA. Las banderas de verificacion no estan en este esquema y no
 * se aceptan del cliente por ninguna via: quien sube el archivo no puede
 * declararse verificado por la plataforma. Eso es lo que separa un testimonio
 * de una comprobacion, que es el corazon de la 0020.
 */
export const acuseHoja = z.object({
    nombre: z.string().trim().min(1).max(120),
    contenido_base64: z.string().min(1),
    /* El Content-Type que declara el cliente. No decide el tipo —eso lo decide
       el contenido— pero queda en la bitacora cuando no corresponde. */
    mime: z.string().trim().max(120).optional(),
    firma_declarada: z.boolean().optional(),
    sello_declarado: z.boolean().optional(),
    /** Quien afirma. Va a la auditoria: "lo declaro Ruben Sandoval el 14 de
     *  marzo" es una respuesta util ante una disputa; "se declaro" no lo es. */
    a_nombre_de: z.string().trim().max(120).optional(),
});
//# sourceMappingURL=movil.js.map